import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  keywords?: string[];
  type?: 'website' | 'article';
  author?: string;
}

export default function SEO({ 
  title = 'GearLoop - Own Less, Explore More',
  description = 'Rent outdoor equipment from locals or earn by renting out your gear. Join the outdoor sharing economy with GearLoop. Find camping gear, bikes, water sports equipment, and more.',
  image = 'https://images.unsplash.com/photo-1517823382935-51bfcb0ec6bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80',
  url = 'https://gearloop.com',
  keywords = [
    'outdoor gear rental',
    'camping equipment',
    'bike rental',
    'water sports gear',
    'outdoor equipment sharing',
    'peer to peer rental',
    'local gear rental',
    'outdoor adventures',
    'camping gear rental',
    'outdoor equipment marketplace',
    'sustainable outdoor gear',
    'sharing economy',
    'adventure gear',
    'outdoor activities',
    'eco-friendly outdoor gear'
  ],
  type = 'website',
  author = 'GearLoop'
}: SEOProps) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'GearLoop',
    description,
    url,
    logo: image,
    sameAs: [
      'https://facebook.com/gearloop',
      'https://twitter.com/gearloop',
      'https://instagram.com/gearloop'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@gearloop.com',
      contactType: 'customer service'
    },
    offers: {
      '@type': 'AggregateOffer',
      description: 'Rent outdoor equipment from locals',
      priceCurrency: 'USD'
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="GearLoop" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@gearloop" />
      <meta name="twitter:creator" content="@gearloop" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#16a34a" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="GearLoop" />
      <meta name="application-name" content="GearLoop" />
      <meta name="format-detection" content="telephone=no" />
      <link rel="canonical" href={url} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}